// TimelineUtilities.js - Composants utilitaires pour l'édition
import React, { useState, useRef, useEffect } from 'react';
import {
    CheckCircle2,
    Circle,
    Clock,
    X,
    Calendar,
    Edit3,
    Check,
    Plus,
    Trash2
} from 'lucide-react';
import { createPortal } from 'react-dom';

// Composant pour le sélecteur de statut avec vos couleurs
export const StatusSelector = ({ status, onChange, disabled = false, size = 'normal' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);
    const [buttonRect, setButtonRect] = useState(null);

    // Options de statut avec vos couleurs personnalisées
    const statusOptions = [
        {
            value: 'a_faire',
            label: 'À faire',
            iconComponent: <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-white" />
        },
        {
            value: 'en_cours',
            label: 'En cours',
            iconComponent: <Clock className="w-4 h-4" style={{ color: '#ffad65' }} />
        },
        {
            value: 'fait',
            label: 'Fait',
            iconComponent: <div className="w-4 h-4 rounded-full border-2 bg-white flex items-center justify-center" style={{ borderColor: '#0e9488' }}><CheckCircle2 className="w-3 h-3" style={{ color: '#0e9488' }} /></div>
        },
        {
            value: 'annule',
            label: 'Annulé',
            iconComponent: <X className="w-4 h-4" style={{ color: '#e57a86' }} />
        }
    ];

    // Mapper le statut d'affichage vers backend
    const mapStatusToBackend = (displayStatus) => {
        const mapping = {
            'À faire': 'a_faire',
            'En cours': 'en_cours',
            'Fait': 'fait',
            'Annulé': 'annule'
        };
        return mapping[displayStatus] || displayStatus;
    };

    const currentBackendStatus = mapStatusToBackend(status);
    const currentStatus = statusOptions.find(s => s.value === currentBackendStatus) || statusOptions[0];

    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setButtonRect(rect);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleScroll = () => {
            if (isOpen) setIsOpen(false);
        };
        if (isOpen) {
            window.addEventListener('scroll', handleScroll, true);
            return () => window.removeEventListener('scroll', handleScroll, true);
        }
    }, [isOpen]);

    const dropdown = isOpen && buttonRect && createPortal(
        <>
            <div
                className="fixed inset-0 z-[25000]"
                onClick={() => setIsOpen(false)}
                data-status-dropdown
            />
            <div
                className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[25001] min-w-32"
                data-status-dropdown
                style={{
                    left: Math.min(buttonRect.left, window.innerWidth - 140),
                    top: buttonRect.bottom + 4 > window.innerHeight - 120
                        ? buttonRect.top - 120
                        : buttonRect.bottom + 4
                }}
            >
                {statusOptions.map(option => (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                            if (!disabled) {
                                onChange(option.value);
                                setIsOpen(false);
                            }
                        }}
                        disabled={disabled}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            currentBackendStatus === option.value ? 'bg-gray-100' : ''
                        }`}
                    >
                        {option.iconComponent}
                        {option.label}
                    </button>
                ))}
            </div>
        </>,
        document.body
    );

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                type="button"
                onClick={(e) => {
                    if (!disabled) {
                        e.stopPropagation();
                        setIsOpen(!isOpen);
                    }
                }}
                disabled={disabled}
                className="cursor-pointer hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                title="Cliquer pour changer le statut"
            >
                {size === 'small' ? 
                    React.cloneElement(currentStatus.iconComponent, { 
                        style: { 
                            ...currentStatus.iconComponent.props.style,
                            width: '12px', 
                            height: '12px' 
                        }
                    }) : 
                    currentStatus.iconComponent
                }
            </button>
            {dropdown}
        </div>
    );
};

// Composant pour l'édition inline de texte
export const InlineTextEditor = ({ value, onChange, placeholder = "", className = "", multiline = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            if (!multiline) {
                inputRef.current.select();
            }
        }
    }, [isEditing, multiline]);

    const handleSave = () => {
        if (editValue.trim() !== value) {
            onChange(editValue.trim());
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !multiline) {
            e.preventDefault();
            handleSave();
        }
        if (e.key === 'Escape') {
            handleCancel();
        }
    };

    if (isEditing) {
        const InputComponent = multiline ? 'textarea' : 'input';
        return (
            <div className="flex items-center gap-2 flex-1">
         <InputComponent
                    ref={inputRef}
                    type={multiline ? undefined : "text"}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSave}
                    className={`flex-1 px-2 py-1 border border-teal-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-0 ${className}`}
                    placeholder={placeholder}
                    rows={multiline ? 2 : undefined}
                    style={{ minWidth: '600px' }}
                />
                <button
                    onClick={handleSave}
                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                    title="Sauvegarder"
                >
                    <Check className="w-3 h-3" />
                </button>
            </div>
        );
    }

    return (
        <div 
            onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
            }}
            className={`flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 ${className}`}
            title="Cliquer pour éditer"
        >
            <span className="flex-1">{value || placeholder}</span>
            <Edit3 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
    );
};

// Composant pour l'édition de date
export const DateEditor = ({ value, onChange, className = "" }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value || '');

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleSave = () => {
        onChange(editValue);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value || '');
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-400" />
                <input
                    type="date"
                    value={formatDateForInput(editValue)}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSave();
                        if (e.key === 'Escape') handleCancel();
                    }}
                    className="text-xs border border-teal-300 rounded px-1 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    autoFocus
                />
            </div>
        );
    }

    return (
        <div 
            onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
            }}
            className={`flex items-center gap-1 cursor-pointer hover:bg-gray-50 rounded px-1 ${className}`}
            title="Cliquer pour modifier la date"
        >
            <Calendar className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-500">
                {value ? formatDateForDisplay(value) : 'Ajouter date'}
            </span>
        </div>
    );
};

// Composant pour une sous-action avec édition (sans date)
export const SubtaskItem = ({ subtask, subtaskIndex, onStatusChange, onTitleChange, onDelete, disabled }) => {
    return (
        <div className="flex items-center gap-2 text-xs p-2 bg-gray-50 rounded group hover:bg-gray-100">
            <StatusSelector
                status={subtask.status}
                onChange={(newStatus) => onStatusChange(subtaskIndex, newStatus)}
                disabled={disabled}
                size="small"
            />
            
            <div className="flex-1">
                <InlineTextEditor
                    value={subtask.title}
                    onChange={(newTitle) => onTitleChange(subtaskIndex, newTitle)}
                    placeholder="Titre de la sous-action"
                    className="text-xs"
                />
            </div>

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(subtaskIndex);
                }}
                className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Supprimer la sous-action"
            >
                <Trash2 className="w-3 h-3" />
            </button>
        </div>
    );
};